import { gsap } from "gsap";
import { useRef } from "react";

import { MOTION_DURATION, MOTION_EASE } from "@/shared/components/motion/motion.tokens";
import { cn } from "@/shared/lib/utils";

import { useIllustrationLoop } from "./useIllustrationLoop";

const SLOTS = 6;
const SLOT_X = [30, 46, 62, 78, 94, 110] as const;
/** Keypad keys tapped for the six digits: indexes into the drawn keys (0–8 are 1–9, 9 is the 0 key). */
const TAPPED_KEYS = [3, 7, 0, 4, 1, 8] as const;
const KEY_COLUMNS = [43, 63, 83] as const;
const KEY_ROWS = [38, 47, 56] as const;

/** Seconds between two digits — a calm typing rhythm, not a race. */
const TYPE_INTERVAL = 0.34;

/**
 * Six digits typed one by one — each tap lights its key and fills its dot — then a check lands, a short hold,
 * and the dots empty for the next round.
 */
function buildPinLoop(svg: SVGSVGElement): gsap.core.Timeline {
  const dots = Array.from(svg.querySelectorAll<SVGCircleElement>("[data-dot]"));
  const flashes = Array.from(svg.querySelectorAll<SVGRectElement>("[data-key-flash]"));
  const check = svg.querySelector<SVGGElement>("[data-check]");
  const timeline = gsap.timeline({ repeat: -1, repeatDelay: 0.5, paused: true });
  if (!check || dots.length !== SLOTS) return timeline;

  gsap.set(dots, { scale: 0, transformOrigin: "50% 50%" });
  gsap.set(flashes, { opacity: 0 });
  gsap.set(check, { scale: 0, opacity: 0, transformOrigin: "50% 50%" });

  dots.forEach((dot, index) => {
    const flash = flashes[TAPPED_KEYS[index]];
    const at = index * TYPE_INTERVAL;
    if (flash) {
      timeline
        .to(flash, { opacity: 0.32, duration: MOTION_DURATION.quick * 0.5, ease: MOTION_EASE.enter }, at)
        .to(flash, { opacity: 0, duration: MOTION_DURATION.quick, ease: MOTION_EASE.enter }, at + 0.12);
    }
    timeline.to(dot, { scale: 1, duration: MOTION_DURATION.quick, ease: MOTION_EASE.pop }, at + 0.04);
  });

  timeline
    .to(
      check,
      { scale: 1, opacity: 1, duration: MOTION_DURATION.enter * 0.6, ease: MOTION_EASE.pop },
      "+=0.12",
    )
    .to(dots, { scale: 1.18, duration: MOTION_DURATION.quick * 0.6, yoyo: true, repeat: 1 }, "<")
    .to({}, { duration: 1.1 })
    .to([...dots, check], {
      scale: 0,
      opacity: 0,
      duration: MOTION_DURATION.quick,
      ease: MOTION_EASE.enter,
      stagger: 0.02,
    })
    .set(dots, { opacity: 1 });
  return timeline;
}

export interface SecurityPinIllustrationProps {
  className?: string;
}

/**
 * The security PIN in miniature: six dots filling as the digits are typed on a small keypad, then a check. It
 * makes "six digits you choose, typed here" visible without a word. Decorative — the card's text already says
 * it. Without motion, six filled dots and the check.
 */
export function SecurityPinIllustration({ className }: SecurityPinIllustrationProps) {
  const ref = useRef<SVGSVGElement>(null);
  useIllustrationLoop(ref, buildPinLoop);

  const keys = [...KEY_ROWS.flatMap((y) => KEY_COLUMNS.map((x) => ({ x, y }))), { x: KEY_COLUMNS[1], y: 65 }];

  return (
    <svg
      ref={ref}
      viewBox="0 0 148 76"
      className={cn("h-[76px] w-auto max-w-full text-primary", className)}
      aria-hidden="true"
      focusable="false"
    >
      {/* Six slots, filled as the PIN is typed */}
      {SLOT_X.map((x) => (
        <g key={x}>
          <circle
            cx={x}
            cy="18"
            r="5.5"
            className="fill-card"
            stroke="currentColor"
            strokeOpacity="0.35"
            strokeWidth="1.3"
          />
          <circle data-dot="" cx={x} cy="18" r="3.4" fill="currentColor" />
        </g>
      ))}

      {/* Done */}
      <g data-check="">
        <circle cx="131" cy="18" r="8" className="fill-success" />
        <path
          d="M127.2 18.2 L130 21 L134.8 15.6"
          fill="none"
          className="stroke-success-foreground"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>

      {/* A small keypad: 1–9 and 0 */}
      {keys.map(({ x, y }, index) => (
        <g key={`${x}-${y}`}>
          <rect
            x={x - 8.5}
            y={y - 3.5}
            width="17"
            height="7"
            rx="3.5"
            className="fill-card"
            stroke="currentColor"
            strokeOpacity="0.28"
            strokeWidth="0.9"
          />
          <rect
            data-key-flash=""
            data-key={index}
            x={x - 8.5}
            y={y - 3.5}
            width="17"
            height="7"
            rx="3.5"
            fill="currentColor"
            opacity="0"
          />
        </g>
      ))}
    </svg>
  );
}
